import { Extension } from '@tiptap/core'
import type { Editor } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { TextSelection, type Selection, type Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view'
import type { Node as PMNode } from '@tiptap/pm/model'
import { numberLevels } from '../../lib/sections'
import { pmHeadings, type PmHeading } from './pm-sections'

/** A § held by someone right now (one unexpired `working` revision). */
export interface SectionLock {
  /** Outline number of the locked §, e.g. "3.2.4". '' locks the whole part. */
  section: string
  /** Display name, rendered on the chip through `data-lock-actor`. */
  actorName: string
  /** Holder's user id — compared with `currentActor` so the holder keeps editing. */
  actor: string
}

/**
 * All of these are read ONCE, when the editor is created. tiptap v3 defines
 * `Extension.options` as a getter that returns a fresh spread on every access, so an option
 * object cannot be mutated in place — a host writing to it would silently write to a throwaway
 * copy. Pass stable callbacks (module-level, or ones that read a ref), and publish changing
 * locks with `setSectionLocks()` instead.
 */
export interface SectionNumbersOptions {
  /** Initial lock set. Later updates go through `setSectionLocks()`. */
  locks: SectionLock[]
  /** Initial § offset — where this part's numbering starts inside its artefact (0 for the
   *  first part). `null` means "not known yet" (the host is still fetching it over HTTP): no
   *  number is numbered speculatively under 0 only to jump moments later, it simply withholds
   *  the decorations until a real value lands. Later updates go through `setSectionOffset()`. */
  offset: number | null
  /** Seed for the decoration memo key; `setSectionLocks()` / `setSectionOffset()` bump it. */
  locksVersion: number
  /** This client's user id, for the lock-holder carve-out. */
  currentActor: string
  /** Click on a § number → the host copies a paste-ready reference. The extension never
   *  touches the clipboard itself; it does select the whole chapter in the editor. */
  onCopyRef: ((number: string, title: string) => void) | null
  /** An edit was rejected because it fell inside someone else's §, so the host can toast.
   *  Rejection is never a silent no-op. */
  onRejected: (() => void) | null
  /** Accessible name for a § number button. Numbers are widgets and deliberately NOT part of
   *  the heading's accessible name, so this is how a screen-reader user reaches them. */
  copyLabel: (number: string, title: string) => string
}

/** The live lock set and § offset, held in ProseMirror state so they are readable from both
 *  `decorations(state)` and `filterTransaction(tr, state)`. */
interface SectionState {
  locks: SectionLock[]
  /** Where this part's numbering starts — see SectionNumbersOptions.offset. `null` = not known yet. */
  offset: number | null
  /** Bumped on every publish — the second half of the decoration memo key. */
  version: number
}

export const sectionNumbersKey = new PluginKey<SectionState>('sectionNumbers')

/** Meta key that unconditionally exempts a transaction from the § lock filter — the escape
 *  hatch for a host applying a write it knows is legitimate (an agent's own edit landing). */
export const SECTION_LOCK_BYPASS = 'sectionLockBypass'

/** Tag a transaction so the § lock filter lets it through untouched. */
export function bypassSectionLock(tr: Transaction): Transaction {
  return tr.setMeta(SECTION_LOCK_BYPASS, true)
}

/** What a publish carries: whichever of the two changed. `offset` is `number | null | undefined`
 *  on purpose — `undefined` (the key omitted, e.g. a locks-only publish) means "leave it", while
 *  an explicit `null` means "the host now knows it does NOT know the offset", which must actually
 *  overwrite a previously-known number rather than falling back to it. */
interface SectionPublish {
  locks?: SectionLock[]
  offset?: number | null
}

function publish(editor: Editor, payload: SectionPublish): void {
  if (editor.isDestroyed) return
  editor.view.dispatch(editor.state.tr.setMeta(sectionNumbersKey, payload))
}

/** Publish the current lock set. This is the ONLY way to update locks after mount — see the
 *  note on SectionNumbersOptions for why the option itself cannot be written to. */
export function setSectionLocks(editor: Editor, locks: SectionLock[]): void {
  publish(editor, { locks })
}

/** Publish the part's § offset, for the same reason (and it changes: a sibling part gaining a
 *  top-level § moves where this one's numbering starts). `null` means "not known yet" — the host
 *  is switching rooms and hasn't heard back from `/v1/sections` for the new one. */
export function setSectionOffset(editor: Editor, offset: number | null): void {
  publish(editor, { offset })
}

/** Ranges (in `doc` position space) currently held by someone. */
interface LockedRange {
  from: number
  to: number
  actorName: string
  actor: string
}

function lockedRanges(doc: PMNode, heads: PmHeading[], numbers: string[], locks: SectionLock[]): LockedRange[] {
  const ranges: LockedRange[] = []
  for (const lock of locks) {
    // '' is the whole part — today's part-wide revision lock, kept back-compatible.
    if (lock.section === '') {
      ranges.push({ from: 0, to: doc.content.size, actorName: lock.actorName, actor: lock.actor })
      continue
    }
    // The § drifted or vanished since the lock was claimed: shade nothing rather than shade
    // the wrong text. The server owns the real isolation and re-anchors by title.
    const idx = numbers.indexOf(lock.section)
    if (idx === -1) continue
    const h = heads[idx]!
    ranges.push({ from: h.from, to: h.to, actorName: lock.actorName, actor: lock.actor })
  }
  return ranges
}

/**
 * The chapter a § number belongs to, as a text selection: from the start of the heading to the
 * end of the last text before the next heading of the same or higher level (or the doc end) —
 * the heading, its body and its sub-sections. `headingFrom` is the position before the heading.
 */
function chapterSelection(doc: PMNode, headingFrom: number): Selection | null {
  const h = pmHeadings(doc).find((x) => x.from === headingFrom)
  if (!h) return null
  return TextSelection.between(doc.resolve(h.from + 1), doc.resolve(h.to))
}

function numberButton(
  number: string,
  title: string,
  options: SectionNumbersOptions,
  view: EditorView,
  getPos: () => number | undefined,
): HTMLButtonElement {
  const el = document.createElement('button')
  el.type = 'button'
  el.className = 'h-num'
  // setAttribute, not the contentEditable IDL property: jsdom does not reflect the latter,
  // so the attribute ProseMirror (and the test) reads would not be there.
  el.setAttribute('contenteditable', 'false')
  el.textContent = number
  el.setAttribute('aria-label', options.copyLabel(number, title))
  // The click — not the browser's mousedown — sets the selection (the whole chapter, below);
  // left alone, a mousedown in the widget would drop a caret or start a drag selection first.
  el.addEventListener('mousedown', (event) => event.preventDefault())
  el.addEventListener('click', (event) => {
    event.preventDefault()
    options.onCopyRef?.(number, title)
    // Also select the chapter (AB#319). Selecting is not an edit, so read-only members get it
    // too. The position is read at click time: the widget sits just inside its heading.
    const pos = getPos()
    if (pos === undefined || view.isDestroyed) return
    const selection = chapterSelection(view.state.doc, pos - 1)
    if (!selection) return
    view.focus()
    view.dispatch(view.state.tr.setSelection(selection))
  })
  return el
}

/**
 * Renders the § outline number on every heading and shades the § other people hold.
 *
 * Numbers are derived from `state.doc` — never from markdown. A `props.decorations(state)`
 * plugin only *has* the PM doc: reading a debounced Y.Text snapshot would blank the numbers on
 * every heading keystroke, and calling getMarkdown() here would re-serialise the whole document
 * per transaction *and* return the live editor's markdown rather than the `state` handed in.
 * Deriving levels from the doc and feeding them to the shared `numberLevels` is what makes the
 * number the member reads equal the number the backend resolves.
 *
 * The number is a widget button rather than a CSS ::before because the MVP-1 workflow is "read
 * the number, hand it to your console agent" — ::before content is neither selectable nor
 * copyable.
 */
export const SectionNumbers = Extension.create<SectionNumbersOptions>({
  name: 'sectionNumbers',

  addOptions() {
    return {
      locks: [],
      offset: 0,
      locksVersion: 0,
      currentActor: '',
      onCopyRef: null,
      onRejected: null,
      copyLabel: (number, title) => `Copy reference to section ${number}${title ? `, ${title}` : ''}`,
    }
  },

  addProseMirrorPlugins() {
    const options = this.options
    // Decorations are recomputed only when the doc or the lock set actually changes; a
    // selection-only transaction keeps the same doc object and reuses the set.
    let memo: { doc: PMNode; version: number; set: DecorationSet } | null = null

    const build = (doc: PMNode, locks: SectionLock[], offset: number | null): DecorationSet => {
      // The offset isn't known yet (the host is still fetching it for the room just opened) —
      // withhold every number and every lock shading rather than render either under a guessed
      // 0, which is exactly the "1/2/3 flash before the real 11/12/13" this null state exists to
      // prevent (AB#30 follow-up). Both depend on the same numbers, so both wait together.
      if (offset === null) return DecorationSet.empty
      const heads = pmHeadings(doc)
      if (heads.length === 0 && locks.length === 0) return DecorationSet.empty
      const numbers = numberLevels(
        heads.map((h) => h.level),
        offset,
      )
      const decos: Decoration[] = []

      heads.forEach((h, idx) => {
        const number = numbers[idx]!
        // `h.from + 1` is inside the heading node, so the button is a child of the <h*>
        // element and `.paper :is(h1,…,h6) .h-num` styles it. side:-1 keeps it ahead of the text.
        decos.push(
          Decoration.widget(h.from + 1, (view, getPos) => numberButton(number, h.title, options, view, getPos), {
            side: -1,
            key: `h-num-${number}`,
          }),
        )
      })

      for (const range of lockedRanges(doc, heads, numbers, locks)) {
        // Decoration.node needs exact node boundaries, so decorate each top-level block that
        // sits wholly inside the §. Never colour alone — `data-lock-actor` carries the name.
        doc.forEach((node, offset) => {
          if (offset < range.from || offset + node.nodeSize > range.to) return
          decos.push(
            Decoration.node(offset, offset + node.nodeSize, {
              class: 'sec-locked',
              'data-lock-actor': range.actorName,
            }),
          )
        })
      }

      return DecorationSet.create(doc, decos)
    }

    return [
      new Plugin<SectionState>({
        key: sectionNumbersKey,

        state: {
          init: () => ({
            locks: options.locks,
            offset: options.offset,
            version: options.locksVersion,
          }),
          apply: (tr, value) => {
            const published = tr.getMeta(sectionNumbersKey) as SectionPublish | undefined
            if (!published) return value
            return {
              locks: published.locks ?? value.locks,
              // `??` would treat a published `null` the same as "key omitted" and keep the OLD
              // offset — exactly wrong for a locks-only publish landing after a room switch set
              // it to null. `offset in published` tells the two apart; `undefined` is the only
              // "leave it" value.
              offset: 'offset' in published && published.offset !== undefined ? published.offset : value.offset,
              version: value.version + 1,
            }
          },
        },

        // Hard read-only enforcement. Honest framing: this is UX, not security — the § is
        // *actually* isolated by the server rejecting out-of-range writes on every /v1 path.
        filterTransaction: (tr, state) => {
          // (b) Never reject a programmatic or remote write. RichEditor's remote-sync path is
          // `editor.commands.setContent(md, { emitUpdate: false })`, which replaces the WHOLE
          // doc and therefore intersects every locked range — rejecting it would stop remote
          // updates from landing and silently desync the editor. Four layers, cheapest first:
          //   - a transaction that changes nothing has nothing to protect (this also lets
          //     setSectionLocks' own meta transaction through);
          //   - our own bypass meta, for a host applying a write it knows is legitimate;
          //   - tiptap's `preventUpdate`, set to true by setContent({ emitUpdate: false });
          //   - `addToHistory: false`, the convention for a remotely-originated write.
          if (!tr.docChanged) return true
          if (tr.getMeta(SECTION_LOCK_BYPASS) === true) return true
          if (tr.getMeta('preventUpdate') === true) return true
          if (tr.getMeta('addToHistory') === false) return true

          const published = sectionNumbersKey.getState(state)
          const locks = published?.locks ?? []
          if (locks.length === 0) return true
          // Offset not known yet — same reasoning as `build()`: resolving a server-issued lock
          // number against a guessed 0 could shade (or miss) the wrong range, so let everything
          // through rather than soft-enforce off a number we know is not the real one.
          if (published?.offset == null) return true

          const heads = pmHeadings(state.doc)
          // Same space as the lock numbers, which the server produced artefact-wide.
          const numbers = numberLevels(
            heads.map((h) => h.level),
            published.offset,
          )
          // (a) The holder keeps editing their own §.
          const ranges = lockedRanges(state.doc, heads, numbers, locks).filter(
            (r) => r.actor !== options.currentActor,
          )
          if (ranges.length === 0) return true

          for (let i = 0; i < tr.steps.length; i++) {
            // Step ranges are in the doc that step sees; move the locked ranges forward to
            // meet them rather than inverting the mapping back.
            const upToStep = tr.mapping.slice(0, i)
            let hit = false
            tr.steps[i]!.getMap().forEach((from, to) => {
              for (const r of ranges) {
                if (from < upToStep.map(r.to, 1) && to > upToStep.map(r.from, -1)) hit = true
              }
            })
            if (hit) {
              // (c) Surface it — a rejection the member cannot see reads as a broken keyboard.
              options.onRejected?.()
              return false
            }
          }
          return true
        },

        props: {
          decorations: (state) => {
            const published = sectionNumbersKey.getState(state) ?? { locks: [], offset: 0, version: 0 }
            if (memo && memo.doc === state.doc && memo.version === published.version) return memo.set
            const set = build(state.doc, published.locks, published.offset)
            memo = { doc: state.doc, version: published.version, set }
            return set
          },
        },
      }),
    ]
  },
})
